import { Analytics } from '@/utils/analytics';

export enum PerfTimingEvent {
  FIRST_SCREEN_VISIBLE = 'first_screen_visible',
  ORDER_CACHE_UPDATE = 'order_context_cache_update',
  ORDER_SUMMARY_VISIBLE = 'order_summary_visible',
}

interface PerfTimingStartContextMap {
  [PerfTimingEvent.FIRST_SCREEN_VISIBLE]: undefined;
  [PerfTimingEvent.ORDER_CACHE_UPDATE]: undefined;
  [PerfTimingEvent.ORDER_SUMMARY_VISIBLE]: {
    entryPoint: string;
  };
}

interface PerfTimingEndContextMap {
  [PerfTimingEvent.FIRST_SCREEN_VISIBLE]: {
    restaurantUUID?: string;
    screenType: string;
    supplierUUID?: string;
  };
  [PerfTimingEvent.ORDER_CACHE_UPDATE]: {
    restaurantUUID: string;
    supplierUUID: string;
  };
  [PerfTimingEvent.ORDER_SUMMARY_VISIBLE]: {
    orderUUID: string;
    restaurantUUID: string;
    smartOrderUUID?: string | null;
    supplierUUID: string;
  };
}

type AnyStartContext = PerfTimingStartContextMap[PerfTimingEvent];

type StartArgs<E extends PerfTimingEvent> =
  PerfTimingStartContextMap[E] extends undefined
    ? []
    : [context: PerfTimingStartContextMap[E]];

interface TimerEntry {
  context?: AnyStartContext;
  startTime: number;
}

const timers: Map<PerfTimingEvent, TimerEntry> = new Map();

export const PerfTiming = {
  /**
   * End a named timer, track the duration via Analytics, and return the duration in milliseconds.
   * Context from start() is merged with context from end(), with end() context taking precedence.
   *
   * @param name - The PerfTimingEvent enum value of the timer to end.
   * @param context - Optional additional properties to include in the analytics event (overrides start context).
   * @returns The duration in milliseconds or null if the timer was not running.
   */
  end: <E extends PerfTimingEvent>(
    name: E,
    context: PerfTimingEndContextMap[E],
  ): number | null => {
    const entry = timers.get(name);
    if (entry === undefined) {
      return null;
    }

    const duration = Math.round(performance.now() - entry.startTime);
    timers.delete(name);

    const startContext = entry.context as AnyStartContext | undefined;
    Analytics.track(`perf_timing.${name}`, {
      ...(startContext ?? {}),
      ...context,
      duration,
    });

    return duration;
  },

  /**
   * Start a named timer. If a timer with this name already exists, it will be overwritten.
   *
   * @param name - The PerfTimingEvent enum value of the timer to start.
   * @param context - Optional context to include in the analytics event when the timer ends.
   */
  start: <E extends PerfTimingEvent>(name: E, ...args: StartArgs<E>): void => {
    /**
     * TS doesn't support a single signature with "required sometimes, optional otherwise"
     * parameters, so we use a tuple rest to keep compile-time enforcement.
     */
    const context = args[0] as AnyStartContext | undefined;
    timers.set(name, {
      context,
      startTime: performance.now(),
    });
  },
};
